<template>
    <common-dropdown :isOpen="isOpen" arrowPositionY="top" arrowPositionX="left">
        <section class="appointment-calendar-slots">
            <template v-if="isSlotsGenerated">
                <header class="appointment-calendar-slots__header">
                    <common-date
                        :date="date"
                        :copy="$tc('pages.referrals.new.steps.schedule.slots_available', availableSlotsCount)"
                    ></common-date>
                </header>

                <div
                    v-if="availableSlotsCount > 0"
                    ref="options"
                    class="appointment-calendar-slots__content appointment-calendar-slots__content--pool"
                >
                    <section v-if="amOptions.length" class="appointment-calendar-slots__group">
                        <h5 class="appointment-calendar-slots__time-of-day">
                            {{ $t('pages.referrals.new.steps.schedule.am') }}
                        </h5>

                        <common-content-spacer height="tiny">
                            <checkbox-input
                                v-for="(slot, index) in amOptions"
                                :key="index"
                                :isDisabled="
                                    totalSlotCount > 2 && !selectedSlots.some((selectedSlot) => isequal(selectedSlot, slot))
                                "
                                :label="slot.label"
                                :value="checked(slot)"
                                @change="selectMultipleSlots(slot)"
                            ></checkbox-input>
                        </common-content-spacer>
                    </section>

                    <section v-if="pmOptions.length" class="appointment-calendar-slots__group">
                        <h5 class="appointment-calendar-slots__time-of-day">
                            {{ $t('pages.referrals.new.steps.schedule.pm') }}
                        </h5>

                        <common-content-spacer height="tiny">
                            <checkbox-input
                                v-for="(slot, index) in pmOptions"
                                :key="index"
                                :isDisabled="
                                    totalSlotCount > 2 && !selectedSlots.some((selectedSlot) => isequal(selectedSlot, slot))
                                "
                                :label="slot.label"
                                :value="checked(slot)"
                                @change="selectMultipleSlots(slot)"
                            ></checkbox-input>
                        </common-content-spacer>
                    </section>
                </div>
                <div v-else ref="options" class="appointment-calendar-slots__content appointment-calendar-slots__content--pool">
                    <section class="appointment-calendar-slots__group">
                        <h1>{{ $t('pages.referrals.new.steps.schedule.not_available') }}</h1>
                    </section>
                </div>
            </template>
        </section>
    </common-dropdown>
</template>

<script>
    import dayjs from 'dayjs'
    import CalendarSlotsMixin from 'mixins/CalendarSlotsMixin'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'
    import isequal from 'lodash.isequal'
    import emitter from 'tiny-emitter/instance'
    import { isSlotEligible } from '@/utils/referralTimeframeUtils'

    export default {
        mixins: [CalendarSlotsMixin],

        components: {
            CheckboxInput,
        },

        props: {
            physician: { type: Object, required: true },
            selectedSlotsByDate: { type: Object, required: false, default: undefined },
        },

        inject: {
            sharedState: {},
            referralTimeframe: { default: null },
            referralCreatedAt: { default: null },
        },

        data() {
            return {
                slots: [],
                selectedSlots: [],
                isequal: isequal,
                isSlotsGenerated: false,
            }
        },

        computed: {
            availableSlotsCount() {
                return this.slots.filter(this.filterFutureSlots).length
            },

            totalSlotCount() {
                return this.sharedState.totalSlotCount
            },

            amOptions() {
                return this.slots
                    .filter(({ to }) => to <= 12 * 60)
                    .filter(this.filterFutureSlots)
                    .map(this.mapSlotToOption)
            },

            pmOptions() {
                return this.slots
                    .filter(({ from }) => from >= 12 * 60)
                    .filter(this.filterFutureSlots)
                    .map(this.mapSlotToOption)
            },
        },

        methods: {
            filterFutureSlots(slot) {
                const slotDateTime = dayjs(`${slot.date} ${slot.startTime}`)

                if (!slotDateTime.isAfter(dayjs())) {
                    return false
                }

                if (this.referralTimeframe && this.referralCreatedAt) {
                    return isSlotEligible(slotDateTime, this.referralTimeframe, this.referralCreatedAt)
                }

                return true
            },

            mapSlotToOption(slot) {
                let start = dayjs().startOf('day').add(slot.from, 'minutes').format('H:mm')
                let end = dayjs().startOf('day').add(slot.to, 'minutes').format('H:mm')
                if (start.length < 5) {
                    start = '0' + start
                }
                if (end.length < 5) {
                    end = '0' + end
                }
                return {
                    label: [start, end].join(' – '),
                    value: slot,
                }
            },

            checked(slot) {
                return this.selectedSlots.some((selectedSlot) => isequal(selectedSlot, slot))
            },

            selectMultipleSlots(slot) {
                if (this.selectedSlots.some((selectedSlot) => isequal(selectedSlot, slot))) {
                    this.selectedSlots = this.selectedSlots.filter((selectedSlot) => !isequal(selectedSlot, slot))
                } else {
                    this.selectedSlots.push(slot)
                }

                this.emitEvent({
                    name: 'selectSlot',
                    payload: {
                        date: this.date.format('YYYY-MM-DD'),
                        slots: this.selectedSlots,
                    },
                })
            },

            generateSlots() {
                const slots = []
                const appointmentLength = this.physician.appointmentLength || 60
                const dayOfWeek = this.date.format('dddd').toLowerCase()
                const workingHours = this.physician.workingHours?.[dayOfWeek]

                if (!workingHours || !workingHours.isWorking) {
                    this.slots = []
                    this.isSlotsGenerated = true
                    return
                }

                const [startHour, startMinute] = workingHours.start.split(':').map(Number)
                const [endHour, endMinute] = workingHours.end.split(':').map(Number)

                const startTimeInMinutes = startHour * 60 + startMinute
                const endTimeInMinutes = endHour * 60 + endMinute

                for (let time = startTimeInMinutes; time < endTimeInMinutes; time += appointmentLength) {
                    const endTime = time + appointmentLength
                    if (endTime > endTimeInMinutes) break

                    const startHours = Math.floor(time / 60)
                    const startMins = time % 60
                    const endHours = Math.floor(endTime / 60)
                    const endMins = endTime % 60

                    slots.push({
                        date: this.date.format('YYYY-MM-DD'),
                        startTime: `${String(startHours).padStart(2, '0')}:${String(startMins).padStart(2, '0')}`,
                        endTime: `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`,
                        from: time,
                        to: endTime,
                    })
                }

                this.slots = slots

                const formattedDate = this.date.format('YYYY-MM-DD')
                let foundSlots = []

                Object.values(this.selectedSlotsByDate || {}).forEach((slots) => {
                    const filteredSlots = slots.filter((slot) => slot.date === formattedDate)
                    foundSlots.push(...filteredSlots)
                })

                if (foundSlots.length > 0) {
                    this.selectedSlots = foundSlots.map((slot) => ({
                        label: slot.label,
                        value: {
                            date: slot.date,
                            endTime: slot.endTime,
                            from: slot.from,
                            startTime: slot.startTime,
                            to: slot.to,
                        },
                    }))
                }

                this.isSlotsGenerated = true
            },
        },

        watch: {
            isOpen(newVal) {
                if (newVal && !this.slots.length) {
                    this.generateSlots()
                }
            },
        },

        mounted() {
            emitter.on('slot-removed', (payload) => {
                if (payload.date === this.date.format('YYYY-MM-DD')) {
                    this.selectedSlots = this.selectedSlots.filter((slot) => slot.label !== payload.slot.label)
                }
            })
        },
    }
</script>
